import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function CampusBulkDeletionDialog({ target, onClose, onDeleted }) {
  const campusIds = useMemo(
    () => [...new Set((target?.campusIds || []).filter(Boolean))],
    [target?.campusIds],
  );
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadingPreview(true);
    api.post("/campuses/delete-preview", {
      scope: target.scope,
      campus_ids: campusIds,
    })
      .then((response) => {
        if (active) setPreview(response.data);
      })
      .catch((error) => {
        if (active) toast.error(error?.response?.data?.detail || "Pratinjau kampus gagal dimuat.");
      })
      .finally(() => {
        if (active) setLoadingPreview(false);
      });
    return () => {
      active = false;
    };
  }, [campusIds, target.scope]);

  const confirmDelete = async () => {
    if (!preview?.count) return;
    setDeleting(true);
    try {
      const response = await api.delete("/campuses", {
        data: {
          scope: target.scope,
          campus_ids: campusIds,
          confirmed: true,
        },
      });
      toast.success(`${response.data.deleted_count} data kampus dihapus.`);
      onDeleted();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Penghapusan kampus gagal.");
    } finally {
      setDeleting(false);
    }
  };

  const title = target.scope === "all" ? "Hapus seluruh kampus?" : "Hapus kampus terpilih?";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="campus-bulk-deletion-dialog"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <AlertTriangle className="h-8 w-8 text-[#DC2626]" />
            <h3 className="mt-3 font-display text-lg font-bold text-[#1F2937]">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-campus-bulk-deletion"
            aria-label="Tutup dialog hapus kampus"
            className="rounded-lg p-2 text-[#6B7280] transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
          Hanya master Kampus dan rekeningnya yang dihapus. Data peserta tidak akan dihapus.
        </p>

        {loadingPreview ? (
          <div className="flex items-center gap-2 py-5 text-sm text-[#6B7280]" data-testid="campus-deletion-preview-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Menghitung data kampus...
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-4">
            <p className="text-sm font-bold text-[#991B1B]" data-testid="campus-deletion-preview-label">
              {preview?.count || 0} kampus akan dihapus
            </p>
            {!!preview?.campuses?.length && (
              <>
                <ul
                  className="mt-2 max-h-36 space-y-1 overflow-y-auto text-xs text-[#991B1B]"
                  data-testid="campus-deletion-preview-list"
                >
                  {preview.campuses.map((campus) => (
                    <li key={campus.id}>
                      {campus.name}{campus.code ? ` · ${campus.code}` : ""}
                    </li>
                  ))}
                </ul>
                {preview.count > preview.campuses.length && (
                  <p className="mt-2 text-xs text-[#991B1B]" data-testid="campus-deletion-preview-summary">
                    Menampilkan {preview.campuses.length} dari {preview.count} kampus.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="cancel-campus-bulk-deletion"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-[#1F2937] transition-colors hover:bg-gray-50"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={loadingPreview || !preview?.count || deleting}
            data-testid="confirm-campus-bulk-deletion"
            className="inline-flex items-center gap-2 rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#B91C1C] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
            <Trash2 className="h-4 w-4" /> Hapus Permanen
          </button>
        </div>
      </div>
    </div>
  );
}